import React, { memo, useCallback, useMemo, useState } from 'react';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';

import fontService, { FontWeight, TextType } from 'services/FontService';
import { sanitizeRichText } from 'services/SanitizeService';
import { Colors } from 'context';
import { Button, ButtonSizes, ToastTypes } from 'atoms';
import {
  ExerciseValues,
  ICareerExercise,
  buildExerciseAnswers,
  buildExerciseInitialValues,
  isExerciseValueEmpty,
  useSubmitCareerExercise,
  useToaster,
} from 'core';

import { ExerciseFieldWidget } from './ExerciseFieldWidget';

export interface ICareerExerciseForm {
  lessonId: string;
  exercise: ICareerExercise;
  /** Урок засчитывается ТОЛЬКО анкетой — тогда форма и есть кнопка «Пройдено». */
  isCompletionGate: boolean;
  onSubmitted: (report: {
    writtenTargets: string[];
    skippedTargets: string[];
    profileCompleteness: number;
  }) => void;
}

/**
 * Анкета-упражнение на уроке карьерного центра (ТЗ 0039).
 *
 * ⚠️ Начальные значения считаются ОДИН РАЗ и не переинициализируются.
 * `careerLesson` ходит с политикой `cache-and-network` и перезапрашивается,
 * пока студент печатает; `enableReinitialize` вычистил бы наполовину
 * заполненную форму у него под руками. Экран опросов этим не страдает только
 * потому, что его запрос выполняется один раз.
 */
const REWARD_TOAST_KEY = 'careerGrowthReward';

/**
 * Показать награду в МОМЕНТ действия (ТЗ 0039, доводка).
 *
 * 🔴 Раньше медаль и промокод молча оседали на сервере: студент узнавал о них,
 * только если сам заходил в «Мой рост» — то есть связь «сделал → получил»
 * рвалась, и геймификация переставала работать ровно там, где должна была.
 *
 * Пустая награда — норма (на этот раз ничего нового не заработано), и тогда
 * тост не показывается: уведомление «вы ничего не получили» никому не нужно.
 */
const showReward = (
  addToast: (key: string, payload: { type: ToastTypes; text: string }) => void,
  t: TFunction,
  grantedTitles: string[],
  promoCodes: string[],
) => {
  const parts = [...grantedTitles, ...promoCodes];
  if (!parts.length) return;
  addToast(REWARD_TOAST_KEY, {
    type: ToastTypes.success,
    text: t('careerCenter.rewardToast', { list: parts.join(', ') }),
  });
};

export const CareerExerciseForm = memo(
  ({ lessonId, exercise, isCompletionGate, onSubmitted }: ICareerExerciseForm) => {
    const { t } = useTranslation();
    const { submitExercise, submitting } = useSubmitCareerExercise(lessonId);
    const { addToast } = useToaster();

    const [values, setValues] = useState<ExerciseValues>(() =>
      buildExerciseInitialValues(exercise),
    );
    const [touched, setTouched] = useState(false);
    const [serverError, setServerError] = useState('');

    const missing = useMemo(
      () =>
        exercise.fields
          .filter((field) => field.required && isExerciseValueEmpty(field, values[field.id]))
          .map((field) => field.id),
      [exercise.fields, values],
    );

    const onSubmit = useCallback(async () => {
      setTouched(true);
      setServerError('');
      if (missing.length) {
        const node = document.getElementById(`exercise-field-${missing[0]}`);
        node?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      const result = await submitExercise(buildExerciseAnswers(exercise, values));
      if (!result.success) {
        setServerError(result.errorMessage || t('careerCenter.exercise.submitFailed'));
        return;
      }
      showReward(addToast, t, result.grantedTitles, result.promoCodes);
      if (result.report) onSubmitted(result.report);
    }, [addToast, exercise, missing, onSubmitted, submitExercise, t, values]);

    return (
      <Wrapper>
        {exercise.intro ? (
          <Intro
            dangerouslySetInnerHTML={{
              __html: sanitizeRichText(exercise.intro),
            }}
          />
        ) : null}

        {exercise.fields.map((field) => {
          const invalid = touched && missing.includes(field.id);
          return (
            <FieldCard key={field.id} id={`exercise-field-${field.id}`}>
              <Label>
                {field.label}
                {field.required ? <Required>*</Required> : null}
              </Label>
              {field.helpText ? <Help>{field.helpText}</Help> : null}
              <ExerciseFieldWidget
                field={field}
                value={values[field.id]}
                onChange={(next) => setValues((prev) => ({ ...prev, [field.id]: next }))}
              />
              {invalid ? <ErrorText>{t('careerCenter.exercise.required')}</ErrorText> : null}
            </FieldCard>
          );
        })}

        {serverError ? <ErrorText>{serverError}</ErrorText> : null}

        <Footer>
          <Button size={ButtonSizes.md} disabled={submitting} onClick={onSubmit}>
            {t(
              isCompletionGate
                ? 'careerCenter.exercise.submitAndComplete'
                : 'careerCenter.exercise.submit',
            )}
          </Button>
          <Note>{t('careerCenter.exercise.note')}</Note>
        </Footer>
      </Wrapper>
    );
  },
);

const Wrapper = styled.div`
  margin-top: 28px;
`;

const Intro = styled.div`
  margin-bottom: 20px;
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
`;

const FieldCard = styled.div`
  padding: 16px;
  margin-bottom: 14px;
  border: 1px solid ${Colors.additional_gray};
  border-radius: 12px;
`;

const Label = styled.p`
  margin: 0 0 4px 0;
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}
`;

const Required = styled.span`
  margin-left: 4px;
  color: ${Colors.signal_red};
`;

const Help = styled.p`
  margin: 0 0 10px 0;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.extraSmall, weight: FontWeight.regular })}
`;

const ErrorText = styled.p`
  margin: 8px 0 0 0;
  color: ${Colors.signal_red};
  ${fontService.text({ type: TextType.extraSmall, weight: FontWeight.medium })}
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 8px;
`;

const Note = styled.span`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.extraSmall, weight: FontWeight.regular })}
`;
